/**
 * Parse tire dimensions from various formats into a standardized format
 * Supports formats like:
 * - 205/55 R16
 * - 205/55R16
 * - 205-55-16
 * - 205 55 16
 * - 205/55/16
 */

const DIMENSION_PATTERNS = [
  // 205/55 R16 or 205/55R16
  /^(\d{3})\/(\d{2})\s?R?(\d{2})$/i,
  // 205-55-16 or 205-55 R16
  /^(\d{3})-(\d{2})-R?(\d{2})$/i,
  // 205 55 16 or 205 55 R16
  /^(\d{3})\s+(\d{2})\s+R?(\d{2})$/i,
  // 205/55/16
  /^(\d{3})\/(\d{2})\/(\d{2})$/,
];

/**
 * Parse tire dimensions from a string
 * @param {string} input - The input string to parse
 * @returns {Object|null} - Parsed dimensions or null if no match
 */
export const parseDimensions = (input) => {
  if (!input || typeof input !== 'string') return null;

  const trimmed = input.trim();
  
  for (const pattern of DIMENSION_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) {
      return {
        width: match[1],
        profile: match[2],
        rim: match[3],
        formatted: `${match[1]}/${match[2]} R${match[3]}`,
        original: trimmed,
      };
    }
  }

  return null;
};

/**
 * Format dimensions to standard format (205/55 R16)
 * @param {string} width - Tire width
 * @param {string} profile - Aspect ratio/profile
 * @param {string} rim - Rim diameter
 * @returns {string} - Formatted dimension string
 */
export const formatDimensions = (width, profile, rim) => {
  return `${width}/${profile} R${rim}`;
};

/**
 * Check if a string contains tire dimensions
 * @param {string} input - The input string to check
 * @returns {boolean} - True if dimensions are detected
 */
export const hasDimensions = (input) => {
  return parseDimensions(input) !== null;
};

/**
 * Extract and format dimensions from a string
 * @param {string} input - The input string
 * @returns {string|null} - Formatted dimensions or null
 */
export const extractFormattedDimensions = (input) => {
  const parsed = parseDimensions(input);
  return parsed ? parsed.formatted : null;
};

/**
 * Suggest formatted dimension if input matches a pattern
 * @param {string} input - The search input
 * @returns {Object|null} - Suggestion object with original and formatted, or null
 */
export const suggestDimensionFormat = (input) => {
  const parsed = parseDimensions(input);
  
  if (!parsed) return null;
  
  // If already in standard format, no suggestion needed
  if (parsed.original === parsed.formatted) return null;
  
  return {
    original: parsed.original,
    suggested: parsed.formatted,
  };
};

/**
 * Format dimensions to compact format (e.g., "205/55 R19" → "2055519")
 * @param {string} dimensions - The dimension string to format
 * @returns {string|null} - Compact formatted dimension string or null
 */
export const formatDimensionsCompact = (dimensions) => {
  if (!dimensions || typeof dimensions !== 'string') return null;
  
  const parsed = parseDimensions(dimensions);
  if (!parsed) return null;
  
  // Remove leading zeros and combine: 205 55 19 → 2055519
  return `${parsed.width}${parsed.profile}${parsed.rim}`;
};

const tyreDimensionsLib = {
  parseDimensions,
  formatDimensions,
  hasDimensions,
  extractFormattedDimensions,
  suggestDimensionFormat,
  formatDimensionsCompact,
};

export default tyreDimensionsLib;

